import { useEffect } from 'react';
import type { z } from 'zod';
import { useLatest } from 'ahooks';
// `react-use-websocket` is a CJS-only module. Under `rolldown-vite` any file
// that imports `react-use-websocket` alongside named imports from its
// pre-bundled dep (directly or via a sibling subpath) gets the default import
// wrapped WITHOUT the standard `!__esModule ? m : m.default` interop dance
// (`const useWebSocket = __vite__cjsImport_X;`), so `useWebSocket` ends up
// as the raw pre-bundled namespace (`{ default: fn, ReadyState, ... }`)
// instead of the function — breaking the hook with
// `useWebSocket is not a function` on the client.
//
// Unwrap the default manually at runtime. `useWebSocketRaw` is either the
// `useWebSocket` function itself (when rolldown-vite DID apply interop) or
// the namespace object whose `.default` is the function (when it did not).
import useWebSocketRaw from 'react-use-websocket';
const useWebSocket: typeof useWebSocketRaw =
    typeof useWebSocketRaw === 'function'
        ? useWebSocketRaw
        : (useWebSocketRaw as { default: typeof useWebSocketRaw }).default;

// WebSocket readyState is standardized by the spec: CONNECTING=0, OPEN=1,
// CLOSING=2, CLOSED=3 (`react-use-websocket` adds UNINSTANTIATED=-1 on top).
// We only need to check for OPEN, so inline the value rather than importing
// `ReadyState` from `react-use-websocket/dist/lib/constants` — that path
// isn't part of the package's public API and would silently break if the
// package ever adds an `exports` map or restructures its `dist/` layout.
const READY_STATE_OPEN = 1;
import { useJwt } from '~/hooks/use-jwt';
import { useAppData } from '~/modules/root';

/**
 * Hook for connecting to the ArchiPro WebSocket server. Handles JWT
 * authentication, heartbeat, exponential-backoff reconnection, and Zod-based
 * message validation/filtering.
 */
export function useWebSocketServer<T extends z.ZodTypeAny>({
    schema,
    onMessage,
}: {
    /**
     * `Zod` schema to validate and filter incoming WebSocket messages.
     */
    schema: T;
    /**
     * Callback invoked with each validated message.
     */
    onMessage?: (message: z.infer<T>) => void;
}) {
    const { jwt: token } = useJwt(
        ['readUser', 'readChat', 'readBuildHub'],
        'WebSocketServer',
    );
    const webSocketUrl = useAppData().webSocketUrl;

    const { lastJsonMessage, readyState, sendMessage, sendJsonMessage } =
        useWebSocket<z.infer<T>>(
            webSocketUrl,
            {
                protocols: token ? ['Authorization', token] : undefined,
                share: true,
                heartbeat: {
                    timeout: 60_000,
                    interval: 10_000,
                },
                filter: (message) => {
                    if (isHeartbeatMessage(message.data)) {
                        return false;
                    }
                    if (typeof message.data !== 'string') {
                        return false;
                    }
                    try {
                        schema.parse(JSON.parse(message.data));
                        return true;
                    } catch {
                        return false;
                    }
                },
                shouldReconnect: () => !!token,
                reconnectAttempts: 20,
                reconnectInterval: (lastAttemptNumber) => {
                    if (lastAttemptNumber === 0) {
                        return 100; // Try to reconnect immediately on first time.
                    } else if (lastAttemptNumber <= 2) {
                        return 2_000; // 2 seconds
                    }
                    // Exponential backoff after the 3rd attempt.
                    const baseDelay = 30_000; // 30 seconds
                    const maxDelay = 3_600_000; // 1 hour
                    const exponent = lastAttemptNumber - 3;
                    return Math.min(
                        baseDelay * Math.pow(2, exponent),
                        maxDelay,
                    );
                },
            },
            !!token,
        );

    const latestOnMessage = useLatest(onMessage);

    useEffect(() => {
        if (lastJsonMessage && latestOnMessage.current) {
            latestOnMessage.current(lastJsonMessage);
        }
    }, [lastJsonMessage, latestOnMessage]);

    return {
        /**
         * The last validated message received from the server.
         */
        lastMessage: lastJsonMessage,
        /**
         * The WebSocket connection is currently open.
         */
        isConnected: readyState === READY_STATE_OPEN,
        /**
         * The ready-state of the WebSocket connection.
         */
        readyState,
        /**
         * Send a message to the server.
         */
        sendMessage,
        /**
         * Send a JSON message to the server.
         */
        sendJsonMessage,
    };
}

/**
 * Check if a message from the WebSocket is a heartbeat message.
 * @param message The message to check.
 * @returns Whether the message is a heartbeat message.
 */
function isHeartbeatMessage(message: unknown) {
    return !message || message === 'ping' || message === 'pong';
}
